import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode, Suspense } from "react";
import { createRoot } from "react-dom/client";
import "./app.css";
import { SectionErrorBoundary } from "./components/dashboard/section-error-boundary";
import { isAccessAdminPath, isAuthenticated } from "./lib/auth";
import { lazyRetry, reloadOnceForChunkError } from "./lib/lazy-retry";
import { APP_NAME } from "./lib/tenant";
import { HomePage } from "./pages/index";
import { LoginPage } from "./pages/login";

// Only admins open it, so it stays out of the dashboard bundle.
const AccessAdminPage = lazyRetry(() => import("./pages/access-admin").then((m) => ({ default: m.AccessAdminPage })));
const AdminPage = lazyRetry(() => import("./pages/admin").then((m) => ({ default: m.AdminPage })));
const HeaderLabPage = lazyRetry(() => import("./pages/header-lab").then((m) => ({ default: m.HeaderLabPage })));
const PrintDocPage = lazyRetry(() => import("./pages/print-doc").then((m) => ({ default: m.PrintDocPage })));
const PrintLabPage = lazyRetry(() => import("./pages/print-lab").then((m) => ({ default: m.PrintLabPage })));
const PrioLabPage = lazyRetry(() => import("./pages/prio-lab").then((m) => ({ default: m.PrioLabPage })));

// Lightweight path switch — keeps the repo's "no router dependency" convention.
// /login → sign-in screen, /admin/access → who can open the dashboard (login +
// per-user admin flag), /admin → data admin panel (own access-code gate), else the
// dashboard. Track A has NO route of its own: the Demand-Sensing engine AND its
// run/workbook console are both sections of the dashboard's Segmentation
// workspace (the console keeps its access-code gate inline, in-section).
if (typeof document !== "undefined") document.title = APP_NAME;

const path = typeof window !== "undefined" ? window.location.pathname : "/";
const isLogin = path.startsWith("/login");
// /admin/access is checked first: it sits behind the normal login (and then the
// server's per-user admin flag), unlike the rest of /admin.
const isAccessAdmin = isAccessAdminPath(path);
const isAdmin = path.startsWith("/admin") && !isAccessAdmin;
const isHeaderLab = path.startsWith("/lab/headers");
const isPrintLab = path.startsWith("/lab/print");
const isLab = path.startsWith("/lab");
// /print/gtm/:report — the server-driven PDF document route. Rendered by
// headless Chrome (data injected on window.__PRINT_DATA__) and viewable in a
// browser for design iteration (falls back to a fetch, then sample data). It
// carries no dashboard chrome and is exempt from the login gate below: it never
// exposes real data on its own — real figures arrive only via Chrome injection
// or an already-authenticated fetch; an anonymous hit renders sample content.
const isPrint = path.startsWith("/print");

// The dashboard is gated by the email/password login. /admin keeps its own
// access-code gate, so it isn't wrapped here. When a token is missing we render
// the login screen in place (no dashboard flash) rather than redirecting, so the
// URL is preserved for after sign-in. The /lab pages sit behind the same login
// gate as the dashboard.
const needsLogin = !isLogin && !isAdmin && !isPrint && !isAuthenticated();

const page =
  isLogin || needsLogin ? (
    <LoginPage />
  ) : isAccessAdmin ? (
    <Suspense fallback={null}>
      <AccessAdminPage />
    </Suspense>
  ) : isAdmin ? (
    <Suspense fallback={null}>
      <AdminPage />
    </Suspense>
  ) : isPrint ? (
    <Suspense fallback={null}>
      <PrintDocPage />
    </Suspense>
  ) : isHeaderLab ? (
    <Suspense fallback={null}>
      <HeaderLabPage />
    </Suspense>
  ) : isPrintLab ? (
    <Suspense fallback={null}>
      <PrintLabPage />
    </Suspense>
  ) : isLab ? (
    <Suspense fallback={null}>
      <PrioLabPage />
    </Suspense>
  ) : (
    <HomePage />
  );

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Dashboard data is refreshed by backend jobs on the order of hours/days,
      // not seconds. A 5-minute stale window means switching between sections
      // (each of which mounts its own query) serves cached data instead of
      // refetching on every mount — the dominant source of UI churn. Sections
      // that need fresher data (search/sentiment/booking ~1h, admin polling
      // ~2s) override this per-hook.
      staleTime: 5 * 60_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// A deploy replaces every hashed chunk; a tab opened before it holds an
// index.html naming files that no longer exist. Vite raises this event when a
// dynamic import or one of its preloaded dependencies fails to load.
window.addEventListener("vite:preloadError", (event) => {
  if (reloadOnceForChunkError()) event.preventDefault();
});

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("#root not found");

createRoot(rootEl).render(
  <StrictMode>
    <SectionErrorBoundary>
      <QueryClientProvider client={queryClient}>{page}</QueryClientProvider>
    </SectionErrorBoundary>
  </StrictMode>,
);
